import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import { NotificationsActive, NotificationsOff } from '@mui/icons-material';
import { InlineStatus } from 'components/InlineStatus';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings';
import { NotificationsDownState } from 'features/weatherAlerts/components/NotificationsDownState';
import { useCreateAlertButton } from 'features/weatherAlerts/hooks/useCreateAlertButton';
import { useWeatherAlertList } from 'features/weatherAlerts/hooks/useWeatherAlertList';
import {
  GenericWeatherStation,
  WeatherAlert,
} from 'features/weatherAlerts/weatherAlerts.types';

type WeatherAlertListProps = {
  fromRoute: string;
  showStationName?: boolean;
  station?: GenericWeatherStation;
};

type EditButtonProps = {
  alert: WeatherAlert;
  fromRoute: string;
};

type AddButtonProps = {
  station?: GenericWeatherStation;
};

const mapEditLinkBase = {
  atmospheric: 'weather_station',
  aqi: 'aqi',
  waterLevel: 'gauge',
};

const EditButton = ({
  alert,
  fromRoute,
}: EditButtonProps): JSX.Element | null => {
  const { t } = useTranslation();

  const { id, station } = alert;
  return (
    <Button
      component={Link}
      to={{
        pathname: `/${mapEditLinkBase[station.weatherStationType]}/${station.id}/alert/${id}`,
        state: { fromRoute },
      }}
      variant="watchduty-text-uppercase"
      sx={{ color: 'accent.main' }}
    >
      {t('common.edit')}
    </Button>
  );
};

const AddButton = ({ station }: AddButtonProps): JSX.Element | null => {
  const { t } = useTranslation();
  const createAlertButtonProps = useCreateAlertButton(station);

  return station ? (
    <Box sx={{ m: 2 }}>
      <Button fullWidth size="large" {...createAlertButtonProps}>
        {t('waterLevel.alerts.addNotification')}
      </Button>
    </Box>
  ) : null;
};

export const WeatherAlertList = (props: WeatherAlertListProps): JSX.Element => {
  const { fromRoute, station, showStationName = true } = props;
  const { isPushEnabled } = useNotificationsSettings();
  const { alerts, isLoading, isError } = useWeatherAlertList({
    stationId: station?.id,
  });

  if (isLoading || isError) {
    return <InlineStatus status={isLoading ? 'loading' : 'error'} />;
  }

  if (!isPushEnabled || !alerts?.length) {
    return (
      <NotificationsDownState isPushEnabled={isPushEnabled} station={station} />
    );
  }

  return (
    <Stack sx={{ gap: 1 }}>
      <List disablePadding>
        {alerts.map((alert) => (
          <ListItem alignItems="center" key={alert.id} divider>
            <ListItemIcon>
              {alert.isEnabled ? (
                <NotificationsActive data-testid="notifications-active" />
              ) : (
                <NotificationsOff
                  data-testid="notifications-off"
                  color="secondary"
                />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                showStationName ? alert.station.name : alert.alertMessage
              }
              secondary={showStationName ? alert.alertMessage : undefined}
              slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
            />
            <EditButton alert={alert} fromRoute={fromRoute} />
          </ListItem>
        ))}
      </List>
      <AddButton station={station} />
    </Stack>
  );
};
